import { useIsDarkMode } from '@flowus/common';
import { CouponCountDown } from '@flowus/common/components/coupon-count-down';
import { cx } from '@flowus/common/cx';
import dayjs from 'dayjs';
import { throttle } from 'lodash-es';
import type { FC } from 'react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from 'src/common/components/icon';
import { useIsExistModalId } from 'src/common/components/next-modal';
import { Tooltip } from 'src/common/components/tooltip';
import { request } from 'src/common/request';
import { PRODUCT_AI_TITLE } from 'src/const/title';
import { useEnableAI } from 'src/hooks/block/use-enable-AI';
import { useGetUnreadCount } from 'src/hooks/public/use-get-unread-count';
import { useCheckLogin } from 'src/hooks/share/use-check-login';
import { useCurrentSpace } from 'src/hooks/space';
import { useCurrentUser, useCurrentUserId } from 'src/hooks/user';
import { Images } from 'src/image';
import { Modals } from 'src/modals';
import { setAppUiState, useIsEditingPage, useServiceCenterRedBadge } from 'src/services/app';
import { useDrawerViewWidth } from 'src/services/app-support/drawer-width';
import { useLocalState } from 'src/services/app-support/local-state';
import { useGetSyncService } from 'src/services/sync';
import { $searchParams } from 'src/utils';
import { bizTracker } from 'src/utils/biz-tracker';
import { judgeSharePage, useGetPageId } from 'src/utils/getPageId';
import {
  useDrawerOpenState,
  useDrawerOpenStateValue,
  useToggleDrawer,
} from 'src/views/main/drawer/shared';
import { OpenSettingFrom } from 'src/views/main/setting-modal/type';
import { useOpenUpgradeSpace } from 'src/views/main/setting-modal/use-open-setting-modal';
import { useGlobalModalQueueEffect } from '../../services/global-modal-queue';
import { TransferButton } from '../transfer-list/transfer-button';
import { useOpenUpgradeSpacePlan } from '../upgrade-space-plan';
import { UpgradeSpacePlanType } from '../upgrade-space-plan/types';

export const ServiceCenter: FC = memo(() => {
  const { drawerWidth } = useDrawerViewWidth();
  const serviceCenterRedBadge = useServiceCenterRedBadge();
  const currentUser = useCurrentUser();
  const isLogin = Boolean(currentUser.uuid);
  const isEditingPage = useIsEditingPage();
  const toggleDrawer = useToggleDrawer();
  const isDrawerOpen = useDrawerOpenStateValue();
  const getUnreadCount = useGetUnreadCount();
  const { enableAI } = useEnableAI();
  const openUpgradeSpace = useOpenUpgradeSpace();
  const spaceId = useCurrentSpace().uuid;
  useIsDarkMode();
  useGlobalModalQueueEffect();
  const isSharePage = judgeSharePage();

  const sync = useGetSyncService();
  const userId = useCurrentUserId();
  const isNotificationsPopupOpen = useIsExistModalId(Modals.MESSAGE_NOTIFICATION);

  const timestamp = useLocalState((state) => {
    if (!state.$showCountDown?.[spaceId]) return null;
    return dayjs(state.$showCountDown[spaceId]?.startTime).add(1, 'hour').valueOf();
  });

  const fetchUnreadCount = useCallback(() => {
    void getUnreadCount().then(({ strategyGuides, messageBoard }) => {
      if (strategyGuides > 0 || messageBoard > 0) {
        setAppUiState({ $serviceCenterRedBadge: true });
      }
    });
  }, [getUnreadCount]);

  useEffect(() => {
    if (isSharePage) return;
    return sync.subscribeNotifications(() => {
      void fetchUnreadCount();
    });
  }, [sync, isNotificationsPopupOpen, userId, fetchUnreadCount, isSharePage]);

  useEffect(() => {
    if (isSharePage) return;
    fetchUnreadCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSharePage]);

  if (!isLogin || isSharePage || $searchParams.print || $searchParams.embed || __PRIVATE__) {
    return null;
  }

  const openHelper = (type: 'ai' | 'service') => {
    bizTracker.event('service_ball_clk');

    if (type === 'ai') {
      void toggleDrawer('ai-chat');
      return;
    }

    if (type === 'service') {
      void toggleDrawer('service-center');
    }
  };

  return (
    <div
      className={cx(
        'service-center fixed right-10 bottom-10 transition-all z-50 select-none space-y-2.5 flex flex-col items-center justify-center',
        isEditingPage ? 'duration-500 opacity-0' : 'duration-100 opacity-100'
      )}
      style={{
        transform: `translateX(${isDrawerOpen ? `-${drawerWidth}px` : '20px'})`,
      }}
    >
      {timestamp && (
        <Tooltip popup="专属限时优惠券" placement="left">
          <CouponCountDown
            timestamp={timestamp}
            img={Images.couponCountDown}
            onClick={() => {
              openUpgradeSpace(OpenSettingFrom.activity);
            }}
          />
        </Tooltip>
      )}
      <TransferButton />
      <ScrollTopButton />
      {enableAI && (
        <Tooltip
          popup={PRODUCT_AI_TITLE}
          placement="right"
          className="w-9 h-9 flex items-center justify-center next-modal animate-click overflow-hidden rounded-full shadow-light-sm"
          onClick={() => {
            openHelper('ai');
          }}
        >
          <Icon name="IcAiFill" size="middle" />
        </Tooltip>
      )}
      <Tooltip
        onClick={() => openHelper('service')}
        placement="right"
        popup="点我查看使用帮助信息"
        className="animate-click btn-helper relative w-9 h-9 flex items-center justify-center next-modal rounded-full shadow-light-sm overflow-visible"
      >
        {!(__BUILD_IN__ || __PRIVATE__) && serviceCenterRedBadge && (
          <div className="absolute right-0 top-0 w-2.5 h-2.5 z-10 bg-red rounded-full" />
        )}
        <Icon size="small" name="IcQuestion" />
      </Tooltip>
    </div>
  );
});

// 知识库的button
export const KnowledgeAIButton: FC = () => {
  const currentUser = useCurrentUser();
  const isLogin = Boolean(currentUser.uuid);
  const { drawerWidth } = useDrawerViewWidth();
  const [isDrawerOpen, setDrawState] = useDrawerOpenState();
  const pageId = useGetPageId();
  const [isKnowledge, setIsKnowledge] = useState(false);
  const checkLogin = useCheckLogin();

  const isShare = judgeSharePage();
  const openHelper = (type: 'ai' | 'service') => {
    bizTracker.event('service_ball_clk');
    if (type === 'ai') {
      if (isDrawerOpen && ['ai-chat-right', 'ai-chat'].includes(isDrawerOpen)) {
        void setDrawState(false);
      } else {
        bizTracker.event('flowus_ai_entrance', { user_id: currentUser.uuid });
        void setDrawState('ai-chat');
      }
    }
  };
  useEffect(() => {
    if (!isLogin) return;
    // 检查是不是知识库的页面
    void request.ai.infraCheckKnowledge(pageId).then((res) => {
      setIsKnowledge(res);
      bizTracker.event('enter_knowledge_page', {
        pageId,
      });
    });
  }, [isLogin, pageId]);

  if (!isShare) {
    return (
      <ScrollTopButton className="fixed right-10 bottom-[150px] transition-all z-50 select-none space-y-2.5 flex flex-col items-center justify-center duration-100 opacity-100" />
    );
  }
  if (!isKnowledge) {
    return (
      <ScrollTopButton className="fixed right-10 bottom-[150px] transition-all z-50 select-none space-y-2.5 flex flex-col items-center justify-center duration-100 opacity-100" />
    );
  }
  return (
    <div
      className={cx(
        'service-center fixed right-10 bottom-[150px] transition-all z-50 select-none space-y-2.5 flex flex-col items-center justify-center duration-100 opacity-100 '
      )}
      style={{
        transform: `translateX(${isDrawerOpen ? `-${drawerWidth}px` : '20px'})`,
      }}
    >
      <Tooltip
        popup={PRODUCT_AI_TITLE}
        placement="right"
        className="w-9 h-9 flex items-center justify-center next-modal animate-click overflow-hidden rounded-full shadow-light-sm"
        onClick={() => {
          if (checkLogin()) {
            openHelper('ai');
            bizTracker.event('open_knowledge_ai', {
              pageId,
            });
          }
        }}
      >
        <Icon name="IcAiFill" size="middle" />
      </Tooltip>
    </div>
  );
};
// 订阅的button
export const SubscribeButton: FC = () => {
  const currentUser = useCurrentUser();
  const isLogin = Boolean(currentUser.uuid);
  const { drawerWidth } = useDrawerViewWidth();
  const [isDrawerOpen, setDrawState] = useDrawerOpenState();
  const [vip, setVip] = useState(false);
  const openUpgradeSpacePlan = useOpenUpgradeSpacePlan();

  const openHelper = (type: 'ai' | 'service') => {
    bizTracker.event('service_ball_clk');
    if (type === 'ai') {
      if (isDrawerOpen && ['ai-chat-right', 'ai-chat'].includes(isDrawerOpen)) {
        void setDrawState(false);
      } else {
        bizTracker.event('flowus_ai_entrance', { user_id: currentUser.uuid });
        void setDrawState('ai-chat');
      }
    }
  };

  useEffect(() => {
    void request.infra.getUserIsVip().then((res) => {
      setVip(res);
    });
  }, []);

  if (!isLogin) {
    return (
      <ScrollTopButton className="fixed right-10 bottom-[150px] transition-all z-50 select-none space-y-2.5 flex flex-col items-center justify-center duration-100 opacity-100" />
    );
  }
  if (!vip) {
    return (
      <ScrollTopButton className="fixed right-10 bottom-[150px] transition-all z-50 select-none space-y-2.5 flex flex-col items-center justify-center duration-100 opacity-100" />
    );
  }

  return (
    <div
      className={cx(
        'service-center fixed right-10 bottom-[150px] transition-all z-50 select-none space-y-2.5 flex flex-col items-center justify-center duration-100 opacity-100'
      )}
      style={{
        transform: `translateX(${isDrawerOpen ? `-${drawerWidth}px` : '20px'})`,
      }}
    >
      <ScrollTopButton />
      <Tooltip
        popup={PRODUCT_AI_TITLE}
        placement="right"
        className="w-9 h-9 flex items-center justify-center next-modal animate-click overflow-hidden rounded-full shadow-light-sm"
        onClick={() => {
          if (vip) {
            openHelper('ai');
          } else {
            openUpgradeSpacePlan({ type: UpgradeSpacePlanType.subscribeAiLimit });
          }
        }}
      >
        <Icon name="IcAiFill" size="middle" />
      </Tooltip>
    </div>
  );
};
export const ScrollTopButton: FC<{ className?: string }> = (props) => {
  const divRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = divRef.current;
    if (!el) return;
    let container = el?.closest('.main-panel');
    if (!container) {
      container = el?.closest('.right-panel');
    }
    const nextSpacePage = container?.querySelector('.next-space-page');
    if (!nextSpacePage) return;
    let timer: ReturnType<typeof setTimeout> | undefined = undefined;
    const scroll = throttle(() => {
      timer && clearTimeout(timer);
      if (nextSpacePage.scrollTop > window.innerHeight) {
        el.style.display = 'block';
        el.style.opacity = '1';
      } else {
        el.style.opacity = '0';
        timer = setTimeout(() => {
          if (nextSpacePage.scrollTop < window.innerHeight) {
            el.style.display = 'none';
          }
        }, 400);
      }
    }, 300);

    nextSpacePage.addEventListener('scroll', scroll);
    return () => {
      nextSpacePage.removeEventListener('scroll', scroll);
    };
  }, []);

  return (
    <div
      ref={divRef}
      className={cx('hidden transition-all duration-300', props.className)}
      style={{
        opacity: '1',
      }}
      onClick={() => {
        let el = divRef.current?.closest('.main-panel');
        if (!el) {
          el = divRef.current?.closest('.right-panel');
        }
        const container = el?.querySelector('.next-space-page');
        if (container) {
          container.scrollTo({ top: 0 });
        }
      }}
    >
      <Tooltip
        popup={'回到顶部'}
        placement="right"
        className="w-9 h-9 flex items-center justify-center next-modal animate-click overflow-hidden rounded-full shadow-light-sm"
      >
        <Icon name="IcArrowUnfold" size="small" className="-rotate-90" />
      </Tooltip>
    </div>
  );
};
